import { useState, useMemo } from "react";
import type { CategoryViewModel, CreateCategoryDto } from "@/lib/domains";
import { SYSTEM_CATEGORY_TYPES, type CategoryType } from "@/lib/domains/category";
import CategoryChip from "./category-chip";

interface CategorySelectorProps {
    categories: CategoryViewModel[];
    selectedCategoryId?: string;
    onSelect: (category: CategoryViewModel) => void;
    onCreateCategory?: (dto: CreateCategoryDto) => Promise<void>;
    recentCategoryIds?: string[];
    showSearch?: boolean;
    columns?: number;
}

export default function CategorySelector({
    categories,
    selectedCategoryId,
    onSelect,
    onCreateCategory,
    recentCategoryIds = [],
    showSearch = true,
    columns = 4,
}: CategorySelectorProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newCategoryType, setNewCategoryType] = useState<CategoryType>('other');
    const [newCustomName, setNewCustomName] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    // Filter categories by search query
    const filteredCategories = useMemo(() => {
        if (!searchQuery) return categories;
        const query = searchQuery.toLowerCase();
        return categories.filter(c => c.displayName.toLowerCase().includes(query));
    }, [categories, searchQuery]);

    // Get recent categories
    const recentCategories = useMemo(() => {
        if (recentCategoryIds.length === 0) return [];
        const categoriesMap = new Map(categories.map(c => [c.id, c]));
        return recentCategoryIds
            .map(id => categoriesMap.get(id))
            .filter(Boolean) as CategoryViewModel[];
    }, [categories, recentCategoryIds]);

    const handleCreateCategory = async () => {
        if (!onCreateCategory) return;

        // Validate name is provided
        if (!newCustomName.trim()) {
            alert('Please enter a category name');
            return;
        }

        setIsCreating(true);
        try {
            await onCreateCategory({
                categoryType: newCategoryType,
                customName: newCustomName.trim(),
            });
            // Reset form
            setNewCategoryType('other');
            setNewCustomName('');
            setShowCreateForm(false);
        } catch (error) {
            console.error('Failed to create category:', error);
            alert('Failed to add category. Please try again.');
        } finally {
            setIsCreating(false);
        }
    };

    const gridColsClass = {
        3: 'grid-cols-3',
        4: 'grid-cols-4',
        5: 'grid-cols-5',
        6: 'grid-cols-6',
    }[columns] || 'grid-cols-4';

    return (
        <div className="space-y-4">
            {/* Search */}
            {showSearch && (
                <div>
                    <input
                        type="text"
                        placeholder="Search categories..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        aria-label="Search categories"
                    />
                </div>
            )}

            {/* Recent Categories */}
            {!searchQuery && recentCategories.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Recently Used</h3>
                    <div className={`grid ${gridColsClass} gap-3`}>
                        {recentCategories.slice(0, 4).map(category => (
                            <CategoryChip
                                key={category.id}
                                category={category}
                                selected={category.id === selectedCategoryId}
                                onClick={() => onSelect(category)}
                                size="md"
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* All Categories */}
            {filteredCategories.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Categories</h3>
                    <div className={`grid ${gridColsClass} gap-3`}>
                        {filteredCategories.map(category => (
                            <CategoryChip
                                key={category.id}
                                category={category}
                                selected={category.id === selectedCategoryId}
                                onClick={() => onSelect(category)}
                                size="md"
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* No results */}
            {searchQuery && filteredCategories.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    No categories found for &quot;{searchQuery}&quot;
                </div>
            )}

            {/* Add Category */}
            {onCreateCategory && (
                <div className="border-t pt-4">
                    {!showCreateForm ? (
                        <button
                            type="button"
                            onClick={() => setShowCreateForm(true)}
                            className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors"
                        >
                            + Add Category
                        </button>
                    ) : (
                        <div className="space-y-3 p-4 border border-gray-200 rounded-lg">
                            <h4 className="font-semibold text-gray-700">Add Category</h4>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category Name *
                                </label>
                                <input
                                    type="text"
                                    value={newCustomName}
                                    onChange={(e) => setNewCustomName(e.target.value)}
                                    placeholder="e.g., Electricity, Rent, Gym"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    maxLength={50}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category Type
                                </label>
                                <select
                                    value={newCategoryType}
                                    onChange={(e) => setNewCategoryType(e.target.value as CategoryType)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {SYSTEM_CATEGORY_TYPES.map(type => (
                                        <option key={type} value={type}>
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Determines icon and color. You can add multiple categories of the same type.
                                </p>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleCreateCategory}
                                    disabled={isCreating || !newCustomName.trim()}
                                    className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isCreating ? 'Adding...' : 'Add'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateForm(false);
                                        setNewCategoryType('other');
                                        setNewCustomName('');
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
